import styled from 'styled-components';
import { PHONE_MEDIA_QUERY } from '../../../../common/responsive';
import { useEffect, useRef, useState } from 'react';
import Button from '../../../../components/Button';
import {
  useGiftForm,
  handleKeyboardDone,
} from '../../../../components/GiftFormContext';
import StepWrapper from '../../../../components/StepWrapper';
import { StaggerContainer, StaggerItem } from '../../../../components/Stagger';
import { selectTokenByNetworkAndAddress } from '../../../../redux/slices/tokenSlice';
import { useSelector } from 'react-redux';
import WalletSwapIcon from '../../../../assets/icons/wallet-swap.svg?react';
import { hapticsImpactLight } from '../../../../common/haptic';
import { BodyLarge } from '../../../../components/Texts';
import { selectPrivyToken } from '../../../../redux/slices/auth';
import {
  EVENT_GIFT_FLOW_PRICE_BUTTON_CLICKED,
  EVENT_GIFT_FLOW_PRICE_CHANGED,
  EVENT_GIFT_FLOW_PRICE_SELECTION_COMPLETED,
  sendEvent,
} from '../../../../common/analytics';
import { selectTokenBalance } from '../../../../redux/slices/walletSlice';

const AmountPillWrapper = styled.div`
  flex: 1;
  width: 100%;
`;

const StyledInput = styled.input`
  height: auto;
  min-height: 48px;
  background-color: transparent;
  display: inline-block;
  width: fit-content;
  flex: 0 0 auto;
  border-radius: 16px;
  text-align: center;
  border: none;
  margin-left: -14px;
  margin-right: -14px;
  font-size: clamp(20px, 9vw, 64px);
  font-weight: 700;
  line-height: 1.2;
  padding: 0;

  color: ${({ theme }) => theme.text.title};
  outline: none;
  @media ${PHONE_MEDIA_QUERY} {
    font-size: clamp(14px, 9vw, 64px);
    min-height: 40px;
  }
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  min-height: 48px;
  position: relative;
  flex: 0 0 auto;
  max-width: 100%;
  padding-left: 14px;
  padding-right: 14px;
  overflow: visible;
  @media ${PHONE_MEDIA_QUERY} {
    min-height: 40px;
  }
`;

const InsideInputContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 100%;
  align-items: center;
  pointer-events: none;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  flex-wrap: nowrap;
  text-align: center;
  font-size: clamp(20px, 9vw, 64px);
  font-weight: 700;
  line-height: 1.2;
  color: ${({ theme }) => theme.text.title};
  text-wrap: nowrap;
  &:focus {
    outline: none;
  }
  @media ${PHONE_MEDIA_QUERY} {
    font-size: clamp(14px, 9vw, 64px);
  }
`;

const AmountButton = styled.button`
  width: 100%;
  border-radius: 16px;
  border: 1px solid
    ${(props) =>
      props.$active
        ? `${props.theme.primary.primary}`
        : `${props.theme.border.primary}`};
  padding: 12px 12px;
  background-color: ${(props) =>
    props.$active
      ? `${props.theme.background.primary}`
      : `${props.theme.background.light}`};
  color: ${({ theme }) => theme.text.title};
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  @media (hover: hover) {
    &:hover:not(:disabled) {
      border-color: ${(props) =>
        props.$active
          ? `${props.theme.primary.primary}`
          : `${props.theme.text.disabled}`};
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AmountButtonContainer = styled.div`
  display: flex;
  gap: 4px;
  width: 100%;
`;

const SwapAmountContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  cursor: pointer;
`;

const AmountsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  justify-content: center;
  align-items: center;
`;

const PaymentSelectorAndButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const FreeIcon = styled.div`
  background-color: ${({ theme }) => theme.primary.light};
  font-size: 10px;
  color: white;
  padding: 2px 6px;
  position: absolute;
  border-radius: 8px;
  right: 8px;
  top: 8px;
`;

const AmountSelector = ({
  onContinue,
  buttonLabel = 'Continue',
  isModal,
  currentStep,
  isBack = false,
}) => {
  const {
    targetTokenAddress,
    network,
    usdAmount,
    setValue,
    setAmountSpecifiedInUsd,
    amountSpecifiedInUsd,
    targetTokenAmount,
  } = useGiftForm();

  const targetToken = useSelector((state) =>
    selectTokenByNetworkAndAddress(state, network, targetTokenAddress),
  );

  const maxBalance = useSelector((state) =>
    selectTokenBalance('solana', targetToken?.address)(state),
  );

  const { showFreeGift } = useGiftForm();

  const tokenSymbolRefElement = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);

  const [fullyRendered, setFullyRendered] = useState(false);
  const inputRef = useRef(null);

  const privyToken = useSelector(selectPrivyToken);
  const isAuthenticated = !!privyToken;

  // Force USD direction for non-authenticated users
  useEffect(() => {
    if (!isAuthenticated && !amountSpecifiedInUsd) {
      setAmountSpecifiedInUsd(true);
    }
  }, [isAuthenticated, amountSpecifiedInUsd, setAmountSpecifiedInUsd]);

  useEffect(() => {
    if (tokenSymbolRefElement?.current) {
      setLabelWidth(tokenSymbolRefElement?.current?.offsetWidth || 0);
    }
  }, [amountSpecifiedInUsd, targetToken?.symbol, targetTokenAmount, usdAmount]);

  useEffect(() => {
    if (!fullyRendered) return;
    if (!inputRef.current) return;
    inputRef.current.focus();
  }, [fullyRendered, inputRef.current]);

  let amountPills = [
    { value: 1, label: '$1' },
    { value: 25, label: '$25' },
    { value: 50, label: '$50' },
  ];

  if (maxBalance > 0) {
    amountPills.push({ value: maxBalance, label: `Max` });
  } else {
    amountPills.push({ value: 100, label: '$100' });
  }

  return (
    <StepWrapper $back={isBack}>
      <StaggerContainer>
        <StaggerItem $delay={1}>
          <AmountsContainer>
            <InputContainer>
              {amountSpecifiedInUsd && (
                <InsideInputContainer>
                  <span>$</span>
                </InsideInputContainer>
              )}
              <StyledInput
                key={tokenSymbolRefElement.current?.offsetWidth}
                style={{
                  maxWidth: `${labelWidth + 42}px`,
                }}
                autoFocus={true}
                ref={inputRef}
                value={amountSpecifiedInUsd ? usdAmount : targetTokenAmount}
                id='amount-input'
                inputMode='decimal'
                enterKeyHint='done'
                onKeyDown={(e) =>
                  handleKeyboardDone(e, inputRef, {
                    onProceed: () => {
                      sendEvent(EVENT_GIFT_FLOW_PRICE_SELECTION_COMPLETED, {
                        is_free_gift: showFreeGift,
                      });
                      onContinue();
                    },
                    isValidForm: true, // Amount is always valid (has default)
                  })
                }
                onChange={(e) => {
                  sendEvent(EVENT_GIFT_FLOW_PRICE_CHANGED, {
                    price: e.target.value,
                    is_free_gift: showFreeGift,
                  });
                  if (amountSpecifiedInUsd) {
                    // Update USD amount - hook will automatically calculate token amount
                    setValue('usdAmount', e.target.value);
                  } else {
                    // Update token amount - hook will automatically calculate USD amount
                    setValue('targetTokenAmount', e.target.value);
                  }
                }}
              />
              {!amountSpecifiedInUsd && (
                <InsideInputContainer>
                  <span>{targetToken?.symbol}</span>
                </InsideInputContainer>
              )}
            </InputContainer>
            <InsideInputContainer style={{ position: 'absolute' }}>
              <span
                style={{ color: 'transparent' }}
                ref={tokenSymbolRefElement}
              >
                {amountSpecifiedInUsd ? usdAmount : targetTokenAmount}
              </span>
            </InsideInputContainer>
            {isAuthenticated && (
              <SwapAmountContainer
                onClick={() => {
                  if (showFreeGift) {
                    return;
                  }
                  if (amountSpecifiedInUsd) {
                    const calculatedAmount = usdAmount / targetToken?.usdPrice;
                    const decimals = targetToken?.decimals ?? 9;
                    const roundedAmount = parseFloat(
                      calculatedAmount.toFixed(decimals),
                    );
                    if (
                      parseFloat(usdAmount).toFixed(2) ===
                      parseFloat(maxBalance * targetToken?.usdPrice).toFixed(2)
                    ) {
                      setValue('targetTokenAmount', maxBalance.toString());
                    } else {
                      setValue('targetTokenAmount', roundedAmount);
                    }
                  } else {
                    const calculatedAmount =
                      targetTokenAmount * targetToken?.usdPrice;
                    const decimals = 2;
                    const roundedAmount = parseFloat(
                      calculatedAmount.toFixed(decimals),
                    );
                    setValue('usdAmount', roundedAmount);
                  }
                  setAmountSpecifiedInUsd(!amountSpecifiedInUsd);
                }}
              >
                <BodyLarge>
                  {amountSpecifiedInUsd
                    ? `${parseFloat(
                        (usdAmount / targetToken?.usdPrice).toFixed(
                          targetToken?.decimals ?? 9,
                        ),
                      )} ${targetToken?.symbol}`
                    : `$${parseFloat(
                        (targetTokenAmount * targetToken?.usdPrice).toFixed(2),
                      )}`}
                </BodyLarge>
                {!showFreeGift && <WalletSwapIcon width={18} height={18} />}
              </SwapAmountContainer>
            )}
          </AmountsContainer>
        </StaggerItem>
        <AmountButtonContainer>
          {amountPills.map((pill, index) => (
            <StaggerItem
              key={pill.value}
              $delay={2 + index}
              style={{ flex: 1 }}
            >
              {showFreeGift && pill.value === 1 && <FreeIcon>Free</FreeIcon>}
              <AmountPillWrapper>
                <AmountButton
                  onClick={() => {
                    if (pill.label === 'Max') {
                      // Set token amount to max (most precise), let USD be calculated
                      setAmountSpecifiedInUsd(false);
                      setValue('targetTokenAmount', maxBalance.toString());
                    } else {
                      setAmountSpecifiedInUsd(true);
                      setValue('usdAmount', pill.value.toString());
                    }
                    hapticsImpactLight();
                    sendEvent(EVENT_GIFT_FLOW_PRICE_BUTTON_CLICKED, {
                      price: pill.value,
                      is_free_gift: showFreeGift,
                    });
                    // Hook will automatically calculate USD amount when token amount changes
                  }}
                >
                  {pill.label}
                </AmountButton>
              </AmountPillWrapper>
            </StaggerItem>
          ))}
        </AmountButtonContainer>
        <StaggerItem $delay={6} style={{ marginTop: '20px', width: '100%' }}>
          <PaymentSelectorAndButtonContainer>
            <Button
              fullWidth
              clickAnimation='bouncy'
              onClick={() => {
                onContinue();
                sendEvent(EVENT_GIFT_FLOW_PRICE_SELECTION_COMPLETED, {
                  is_free_gift: showFreeGift,
                });
              }}
            >
              {buttonLabel}
            </Button>
          </PaymentSelectorAndButtonContainer>
        </StaggerItem>
      </StaggerContainer>
    </StepWrapper>
  );
};

export default AmountSelector;
